import * as React from 'react';
import {
  getGetAccountBusinessLookupStatusQueryKey,
  useGetAccountBusinessLookupStatus,
  useLookupAccountBusiness,
  type AccountBusinessLookupResult
} from '@cdf/api-client-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface Props {
  open: boolean;
  allowed: boolean;
  disabled: boolean;
  ownerKey: string;
  tenantId?: string;
  entityId?: string;
  name: string;
  website: string;
  notes: string;
  location: string;
  onLocationChange: (location: string) => void;
  onApply: (values: { website?: string; notes?: string }) => void;
}

/** Optional public research. Results never mutate the account until explicit field selection/application. */
export function AccountBusinessLookup(props: Props) {
  const { open, allowed, disabled, ownerKey, tenantId, entityId, name, website, notes, location, onLocationChange, onApply } = props;
  const [result, setResult] = React.useState<AccountBusinessLookupResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [candidateId, setCandidateId] = React.useState('');
  const [fields, setFields] = React.useState({ website: false, notes: false });
  const generation = React.useRef(0);
  const inFlight = React.useRef(false);
  const appliedTerms = React.useRef<string | null>(null);
  const mutation = useLookupAccountBusiness({ mutation: { retry: false } });
  const status = useGetAccountBusinessLookupStatus({ query: {
    queryKey: [...getGetAccountBusinessLookupStatusQueryKey(), ownerKey],
    enabled: open && allowed, retry: false, staleTime: 0
  } });
  const terms = JSON.stringify([name.trim(), website.trim(), location.trim()]);
  React.useLayoutEffect(() => {
    generation.current++;
    inFlight.current = false;
    setBusy(false);
    // Keep reviewed evidence when this component deliberately applies its own homepage suggestion.
    // User edits, permission/owner/visibility changes still retire it.
    if (!open || !allowed || appliedTerms.current !== terms) {
      setResult(null); setError(null); setCandidateId('');
      setFields({ website: false, notes: false });
    }
    appliedTerms.current = null;
    return () => { generation.current++; inFlight.current = false; };
  }, [open, allowed, ownerKey, terms]);
  const ready = allowed && status.isSuccess && !status.isError && !status.isFetching &&
    status.data.approved && status.data.enabled && status.data.configured && status.data.liveSmokeTested &&
    status.data.limits.creditsUsedThisMonth < status.data.limits.monthlyCreditCap;
  const candidate = result?.candidates.find(item => item.id === candidateId);

  async function lookup() {
    if (inFlight.current || disabled || !ready || !open || name.trim().length < 3) return;
    const token = ++generation.current;
    inFlight.current = true; setBusy(true); setError(null); setResult(null); setCandidateId('');
    setFields({ website: false, notes: false });
    const requestId = crypto.randomUUID();
    try {
      const response = await mutation.mutateAsync({ data: {
        requestId, name: name.trim(),
        ...(website.trim() ? { website: website.trim() } : {}),
        ...(location.trim() ? { location: location.trim() } : {})
      } });
      if (generation.current !== token) return;
      if (response.requestId !== requestId || response.tenantId !== tenantId || response.entityId !== entityId) {
        setError('The lookup belongs to a different account context. Your draft has not changed.');
        return;
      }
      setResult(response);
    } catch (cause) {
      if (generation.current === token) setError(cause instanceof Error ? cause.message : 'Business information could not be found. Continue manually.');
    } finally {
      if (generation.current === token) {
        inFlight.current = false; setBusy(false);
        void status.refetch();
      }
    }
  }
  function apply() {
    if (!candidate || disabled || !allowed || !open) return;
    const values = {
      ...(fields.website && candidate.suggestions.website ? { website: candidate.suggestions.website } : {}),
      ...(fields.notes ? { notes: candidate.suggestions.notes } : {})
    };
    if (!Object.keys(values).length) return;
    const replacements = [
      values.website && website.trim() ? 'Website: ' + website : '',
      values.notes && notes.trim() ? 'Notes: ' + notes : ''
    ].filter(Boolean);
    if (replacements.length && !window.confirm('Replace these current draft values with the selected public suggestions?\n\n' + replacements.join('\n'))) return;
    if (values.website && values.website !== website) appliedTerms.current = JSON.stringify([name.trim(), values.website.trim(), location.trim()]);
    onApply(values);
    setFields({ website: false, notes: false });
  }
  if (!open) return null;
  return <section aria-label="Optional business web lookup" className="space-y-3 rounded-md border p-3 text-sm">
    <p className="font-medium">Find business information on the web</p>
    <p className="text-muted-foreground">Optional. Only the public business name, website and location below are searched. Document contents and other account details are never sent.</p>
    <div className="grid gap-1.5">
      <Label htmlFor="account-lookup-location">Public location hint (optional)</Label>
      <Input id="account-lookup-location" value={location} maxLength={80} disabled={disabled}
        onChange={event => onLocationChange(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') event.preventDefault(); }} placeholder="City or country" />
    </div>
    <p className="break-words">Search terms: {[name.trim(), website.trim(), location.trim()].filter(Boolean).join(' · ') || 'Enter a public business name above.'}</p>
    <p className="text-muted-foreground">One search attempt uses up to 1 configured search credit, including an attempt you stop waiting for. No automatic retries.</p>
    {status.data && allowed && <p className="text-muted-foreground">
      {status.data.limits.creditsUsedThisMonth} of {status.data.limits.monthlyCreditCap} monthly web searches used.{' '}
      <a href={status.data.pricingUrl} target="_blank" rel="noreferrer" className="underline">Configured provider pricing</a>
    </p>}
    {!allowed ? <p>Web lookup is unavailable with your current permissions. You can enter details manually.</p>
      : status.isError ? <div role="status">Web lookup availability could not be checked. Your draft is retained.{' '}
        <Button type="button" size="sm" variant="outline" disabled={disabled || status.isFetching} onClick={() => void status.refetch()}>Check lookup availability</Button></div>
      : !status.data ? <p role="status">Checking web lookup availability…</p>
      : !ready && !status.isFetching ? <p role="status">{status.data.message} You can continue manually.</p> : null}
    <div className="flex flex-wrap gap-2">
      <Button type="button" variant="outline" disabled={disabled || busy || !ready || name.trim().length < 3}
        onClick={() => void lookup()}>{busy ? 'Searching public business information…' : 'Find business information on the web'}</Button>
      {busy && <Button type="button" variant="outline" disabled={disabled} onClick={() => {
        generation.current++; inFlight.current = false; setBusy(false);
        setError('Stopped waiting. The provider may still use a credit. No results will be applied; another search is a new attempt.');
      }}>Stop waiting for lookup</Button>}
    </div>
    {error && <p role="status">{error} You can continue entering account details manually.</p>}
    {result && <div className="space-y-3" aria-live="polite">
      <p>{result.message}</p><p className="break-words">Searched: {result.query} · Web searches used: {result.creditsUsed}</p>
      {result.state === 'no_match' && <p>No matching public information. Continue manually.</p>}
      {result.candidates.length > 0 && <fieldset className="space-y-2" disabled={disabled || !allowed}>
        <legend className="mb-2 font-medium">{result.state === 'ambiguous' ? 'Several possible businesses: choose a source to review' : 'Choose a source to review'}</legend>
        {result.candidates.map(item => <div key={item.id} className="space-y-1 rounded-md border p-2">
          <label className="flex items-start gap-2"><input type="radio" name="account-business-source" value={item.id}
            checked={candidateId === item.id} onChange={() => { setCandidateId(item.id); setFields({ website: false, notes: false }); }} />
            <span>{item.title}</span></label>
          <a href={item.sourceUrl} target="_blank" rel="noreferrer" className="block break-all underline">View source: {item.sourceUrl}</a>
          <p className="whitespace-pre-wrap break-words">{item.excerpt}</p>
          <p className="text-xs text-muted-foreground">Retrieved {item.retrievedAt}</p>
        </div>)}
      </fieldset>}
      {candidate && <fieldset disabled={disabled || !allowed} className="space-y-2">
        <legend className="font-medium">Select suggestions to apply to your editable draft</legend>
        <p>Unverified public information. Confirm this source describes the right business.</p>
        {candidate.suggestions.website && <label className="block break-words"><input type="checkbox" checked={fields.website}
          onChange={event => setFields(current => ({ ...current, website: event.target.checked }))} /> Website: {candidate.suggestions.website}</label>}
        <label className="block whitespace-pre-wrap break-words"><input type="checkbox" checked={fields.notes}
          onChange={event => setFields(current => ({ ...current, notes: event.target.checked }))} /> Notes: {candidate.suggestions.notes}</label>
        <Button type="button" size="sm" variant="outline" disabled={!fields.website && !fields.notes} onClick={apply}>Apply selected suggestions</Button>
      </fieldset>}
    </div>}
  </section>;
}
